import { AutoScroller, Feedback, PointerActivationConstraints } from "@dnd-kit/dom"
import { DragDropProvider, PointerSensor, useDraggable, useDroppable } from "@dnd-kit/solid"
import { createContext, createUniqueId, onCleanup, useContext, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { useSidebar } from "./context"
import { sidebarKey, type SidebarProjectRef, type SidebarRef } from "./schema"

type Item = { ref: SidebarRef; project?: SidebarProjectRef }
export type SidebarDrop =
  | { kind: "pinned"; order: SidebarRef[]; anchor?: SidebarRef }
  | {
      kind: "projects"
      order: SidebarRef[]
      anchor?: SidebarRef
      sessions?: { project: SidebarProjectRef; order: SidebarRef[] }
    }
  | { kind: "sessions"; project: SidebarProjectRef; order: SidebarRef[]; anchor?: SidebarRef }

function destination(item: Item, target: SidebarDrop): SidebarDrop {
  if (item.ref.type === "session" && target.kind === "projects" && target.sessions)
    return { kind: "sessions", ...target.sessions }
  return target
}

function accepts(item: Item, target: SidebarDrop) {
  target = destination(item, target)
  if (target.kind === "pinned") return true
  if (target.kind === "projects") return item.ref.type === "project"
  return item.ref.type === "session" && !!item.project && sidebarKey(item.project) === sidebarKey(target.project)
}

function inserted(target: SidebarDrop, item: SidebarRef, after: boolean) {
  const key = sidebarKey(item)
  const order = target.order.filter((entry) => sidebarKey(entry) !== key)
  const index = target.anchor ? order.findIndex((entry) => sidebarKey(entry) === sidebarKey(target.anchor!)) : -1
  order.splice(index < 0 ? order.length : index + Number(after), 0, item)
  return order
}

function createSidebarDrag() {
  const sidebar = useSidebar()
  const items = new Map<string, () => Item>()
  const targets = new Map<string, () => SidebarDrop>()
  let pendingSource: Item | undefined
  onCleanup(() => {
    pendingSource = undefined
  })
  const [state, setState] = createStore({
    source: undefined as Item | undefined,
    over: undefined as string | undefined,
    after: false,
    suppressClick: false,
  })
  return {
    state,
    setState,
    registerItem(id: string, read: () => Item) {
      items.set(id, read)
      onCleanup(() => items.delete(id))
    },
    registerTarget(id: string, read: () => SidebarDrop) {
      targets.set(id, read)
      onCleanup(() => targets.delete(id))
    },
    accepts(id: string, target: SidebarDrop) {
      const item = items.get(id)?.()
      return !!item && accepts(item, target)
    },
    start(id: string) {
      const item = items.get(id)?.()
      pendingSource = item
      // Feedback measures the source after the drag-start batch. Revealing an
      // empty Pinned section sooner shifts that origin away from the pointer.
      queueMicrotask(() => {
        if (item && pendingSource === item) setState("source", item)
      })
    },
    mark(id: string | undefined, y: number, element?: Element) {
      const bounds = element?.getBoundingClientRect()
      setState({ over: id, after: !!bounds && y > bounds.top + bounds.height / 2 })
    },
    finish(id: string | undefined, canceled: boolean) {
      const item = state.source ?? pendingSource
      pendingSource = undefined
      const drop = id ? targets.get(id)?.() : undefined
      const target = item && drop ? destination(item, drop) : undefined
      if (
        !canceled &&
        item &&
        target &&
        accepts(item, target) &&
        sidebarKey(item.ref) !== (target.anchor && sidebarKey(target.anchor))
      ) {
        const order = inserted(target, item.ref, state.after)
        if (target.kind === "pinned") {
          sidebar.pin(item.ref)
          sidebar.reorderPinned(order.map(sidebarKey))
        }
        if (target.kind === "projects") {
          sidebar.unpin(item.ref)
          sidebar.reorderProjectList(order.map(sidebarKey))
        }
        if (target.kind === "sessions") {
          sidebar.unpin(item.ref)
          sidebar.reorderSessions(
            target.project,
            order.flatMap((ref) => (ref.type === "session" ? [ref.sessionID] : [])),
          )
        }
      }
      setState({ source: undefined, over: undefined, after: false, suppressClick: true })
    },
  }
}

const SidebarDragContext = createContext<ReturnType<typeof createSidebarDrag>>()
export function useSidebarDrag() {
  const value = useContext(SidebarDragContext)
  if (!value) throw new Error("Sidebar drag provider is missing")
  return value
}

export function SidebarDragProvider(props: ParentProps) {
  const drag = createSidebarDrag()
  return (
    <SidebarDragContext.Provider value={drag}>
      <DragDropProvider
        sensors={[
          PointerSensor.configure({
            activationConstraints: (event) => [
              new PointerActivationConstraints.Distance({ value: event.pointerType === "touch" ? 8 : 5 }),
            ],
            preventActivation: (event) =>
              event.target instanceof Element &&
              !!event.target.closest(
                'input, [contenteditable="true"], [data-sidebar-no-drag], [data-action="session-navigation-project-menu"], [data-action="session-navigation-project-new"], [data-action="session-navigation-session-menu"]',
              ),
          }),
        ]}
        plugins={(defaults) => [
          ...defaults,
          AutoScroller.configure({ acceleration: 8, threshold: { x: 0, y: 0.08 } }),
          Feedback.configure({ dropAnimation: null }),
        ]}
        onDragStart={(event) => {
          if (event.operation.source) drag.start(String(event.operation.source.id))
        }}
        onDragMove={(event) =>
          drag.mark(
            event.operation.target ? String(event.operation.target.id) : undefined,
            event.operation.position.current.y,
            event.operation.target?.element,
          )
        }
        onDragOver={(event) =>
          drag.mark(
            event.operation.target ? String(event.operation.target.id) : undefined,
            event.operation.position.current.y,
            event.operation.target?.element,
          )
        }
        onDragEnd={(event) =>
          drag.finish(event.operation.target ? String(event.operation.target.id) : undefined, event.canceled)
        }
      >
        <div
          data-component="sidebar-collections"
          ref={(element) => {
            const down = () => drag.setState("suppressClick", false)
            const click = (event: MouseEvent) => {
              if (!drag.state.suppressClick || event.detail === 0) return
              event.preventDefault()
              event.stopPropagation()
              drag.setState("suppressClick", false)
            }
            element.addEventListener("pointerdown", down, true)
            element.addEventListener("click", click, true)
            onCleanup(() => {
              element.removeEventListener("pointerdown", down, true)
              element.removeEventListener("click", click, true)
            })
          }}
        >
          {props.children}
        </div>
      </DragDropProvider>
    </SidebarDragContext.Provider>
  )
}

export function SidebarDragItem(
  props: ParentProps<{ item: SidebarRef; project?: SidebarProjectRef; target: SidebarDrop }>,
) {
  const drag = useSidebarDrag()
  const id = createUniqueId()
  const targetID = `drop-${id}`
  const draggable = useDraggable({ id })
  const droppable = useDroppable({ id: targetID, accept: (source) => drag.accepts(String(source.id), props.target) })
  drag.registerItem(id, () => ({ ref: props.item, project: props.project }))
  drag.registerTarget(targetID, () => props.target)
  return (
    <div
      ref={(element) => {
        draggable.ref(element)
        droppable.ref(element)
      }}
      data-sidebar-item
      data-sidebar-key={sidebarKey(props.item)}
      data-dragging={draggable.isDragging()}
      data-drop-position={
        drag.state.over === targetID &&
        drag.state.source &&
        sidebarKey(drag.state.source.ref) !== sidebarKey(props.item)
          ? drag.state.after
            ? "after"
            : "before"
          : undefined
      }
    >
      {props.children}
    </div>
  )
}

export function SidebarDropZone(props: ParentProps<{ target: SidebarDrop; name: string; class?: string }>) {
  const drag = useSidebarDrag()
  const id = createUniqueId()
  const droppable = useDroppable({
    id,
    accept: (source) => drag.accepts(String(source.id), props.target),
    collisionPriority: -1,
  })
  drag.registerTarget(id, () => props.target)
  return (
    <div
      ref={droppable.ref}
      class={props.class}
      data-sidebar-drop={props.name}
      data-drop-active={droppable.isDropTarget()}
    >
      {props.children}
    </div>
  )
}
